export interface HourlyStep {
    hour: number;
    setMins: number;
    flowRate: number;
}

interface ScheduleParams {
    deviceId: string;
    hourlySteps: HourlyStep[];
    patientName: string;
    medicineName: string;
    bedNo: string;
    volumeConsumed: number;
    accessToken: string;
    apiKey: string;
    flowRateApiUrl: string;
    pubFlowRateApiUrl: string;
    onStageChange?: (stepIndex: number, newFlowRate: number) => void;
}

const activeScheduleTimers: Map<string, ReturnType<typeof setTimeout>[]> = new Map();

export const clearHourlySchedule = (deviceId: string) => {
    if (activeScheduleTimers.has(deviceId)) {
        const timers = activeScheduleTimers.get(deviceId);
        if (timers) {
            timers.forEach((timer) => clearTimeout(timer));
        }
        activeScheduleTimers.delete(deviceId);
    }
};

export const startHourlyFlowRateSchedule = (params: ScheduleParams) => {
    const {
        deviceId,
        hourlySteps,
        patientName,
        medicineName,
        bedNo,
        volumeConsumed,
        accessToken,
        apiKey,
        flowRateApiUrl,
        pubFlowRateApiUrl,
        onStageChange,
    } = params;

    clearHourlySchedule(deviceId);

    if (!hourlySteps || hourlySteps.length <= 1) {
        return;
    }

    const scheduledTimers: ReturnType<typeof setTimeout>[] = [];
    let cumulativeDelayMs = 0;

    for (let i = 1; i < hourlySteps.length; i++) {
        const prevStep = hourlySteps[i - 1];
        const stepDurationMs = Number(prevStep.setMins || 60) * 60 * 1000;
        cumulativeDelayMs += stepDurationMs;

        const currentStep = hourlySteps[i];

        const timer = setTimeout(async () => {
            console.log(
                `Executing hourly stage ${currentStep.hour} for device ${deviceId} with flow rate ${currentStep.flowRate}`
            );

            const requestBody = {
                device_id: deviceId,
                RPM: currentStep.flowRate,
                T_L_Time: currentStep.setMins,
                patient_name: patientName || '',
                medicine_name: medicineName || '',
                bed_no: bedNo || '',
                Volume: volumeConsumed || 0,
                current_rpm: prevStep.flowRate || 0,
                current_t_qua: 0,
                current_t_l_time: 0,
                hourly_steps: hourlySteps,
                set_mins: currentStep.setMins,
                current_stage_index: i,
            };

            const requestBodyPub = {
                device_id: deviceId,
                RPM: currentStep.flowRate,
                T_L_Time: currentStep.setMins,
                set_mins: currentStep.setMins,
            };

            try {
                if (flowRateApiUrl) {
                    await fetch(flowRateApiUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'x-api-key': apiKey,
                            'access-token': accessToken,
                        },
                        body: JSON.stringify(requestBody),
                    });
                }

                if (pubFlowRateApiUrl) {
                    await fetch(pubFlowRateApiUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'x-api-key': apiKey,
                            'access-token': accessToken,
                        },
                        body: JSON.stringify(requestBodyPub),
                    });
                }

                if (onStageChange) {
                    onStageChange(i, currentStep.flowRate);
                }
            } catch (error) {
                console.error(
                    `Error executing scheduled hourly flow rate change for device ${deviceId}:`,
                    error
                );
            }
        }, cumulativeDelayMs);

        scheduledTimers.push(timer);
    }

    activeScheduleTimers.set(deviceId, scheduledTimers);
};
